import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSession } from '../../contexts/SessionContext';
import { Lock, Eye, EyeOff, ShieldCheck, LogOut, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import hospitalConfig from '../../config/hospitalConfig';

const LockScreenOverlay = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const { isLocked, unlockStation, handleHardLogout } = useSession();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Auto-focus the password field when locked
  useEffect(() => {
    if (isLocked) {
      setPassword('');
      setError('');
      setShowPassword(false);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isLocked]);

  if (!isLocked) return null;

  // While auth state rehydrates on page refresh, shield workstation with lock screen loader
  if (authLoading || !user) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Workstation Locked"
        className="fixed inset-0 z-[9999] bg-slate-950/95 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-6 overflow-y-auto select-none animate-fade-in"
      >
        <div className="w-full max-w-sm bg-slate-900/90 border border-slate-800 rounded-3xl shadow-2xl p-6 text-white text-center flex flex-col items-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mb-4 text-amber-400">
            <Lock className="w-7 h-7 animate-pulse" />
          </div>
          <h2 className="text-base font-bold text-white mb-1">Workstation Locked</h2>
          <p className="text-xs text-slate-400 mb-4">Restoring secure clinical session...</p>
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  const handleUnlock = async (e) => {
    e?.preventDefault();
    if (!password.trim()) {
      setError('Please enter your account password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await unlockStation(password);
      if (!res.success) {
        setError(res.error || 'Incorrect password. Please verify and try again.');
        inputRef.current?.select();
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatRole = (role) => {
    if (!role) return 'Staff';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getRoleBadgeColor = (role) => {
    const map = {
      admin: 'bg-red-500/20 text-red-300 border-red-500/30',
      doctor: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      nurse: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      matron: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      pharmacist: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      lab_technician: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
      accountant: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
      receptionist: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
    };
    return map[role] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Workstation Locked"
      className="fixed inset-0 z-[9999] bg-slate-950/90 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-6 overflow-y-auto select-none animate-fade-in"
    >
      <div className="w-full max-w-md bg-slate-900/90 border border-slate-800 rounded-3xl shadow-2xl p-6 sm:p-8 text-white relative overflow-hidden backdrop-saturate-150">
        {/* Subtle background glow effect */}
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Top Header Branding */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-800/80">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight text-slate-100">
                {hospitalConfig?.name || 'KMCH CommunityCare'}
              </h1>
              <p className="text-[10px] text-slate-400 font-mono">Healthcare Security Shield</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold">
            <Lock className="w-3.5 h-3.5" />
            <span>Station Locked</span>
          </div>
        </div>

        {/* Clinician Identity Profile */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="relative mb-3">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-slate-800 to-slate-700 border-2 border-slate-600 flex items-center justify-center overflow-hidden shadow-inner">
              {userProfile?.photo_url ? (
                <img
                  src={userProfile.photo_url}
                  alt={userProfile?.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl font-bold text-slate-200">
                  {userProfile?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-500 border-2 border-slate-900 flex items-center justify-center text-slate-950">
              <Lock className="w-3.5 h-3.5" />
            </div>
          </div>

          <h2 className="text-lg font-bold text-white tracking-tight">
            {userProfile?.full_name || 'Authenticated Clinician'}
          </h2>
          <p className="text-xs text-slate-400 font-mono mb-2">{user?.email}</p>

          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${getRoleBadgeColor(userProfile?.role)}`}>
              {formatRole(userProfile?.role)}
            </span>
            {userProfile?.employee_id && (
              <span className="text-[11px] text-slate-400 font-mono">
                #{userProfile.employee_id}
              </span>
            )}
          </div>
        </div>

        {/* Informational reassurance notice */}
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 mb-6 text-center text-xs text-slate-300 leading-relaxed">
          Patient records are shielded. Re-enter your password to resume clinical session without losing unsaved drafts.
        </div>

        {/* Password Re-authentication Form */}
        <form onSubmit={handleUnlock} className="space-y-4">
          <div>
            <div className="relative">
              <input
                ref={inputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password to unlock"
                disabled={loading}
                autoComplete="current-password"
                className="w-full bg-slate-950/80 border border-slate-700 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 pr-11 transition-all outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors p-1"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {error && (
              <div className="mt-2.5 flex items-center gap-2 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 px-3 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !password.trim()}
            className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-semibold py-3 px-4 rounded-xl text-sm flex items-center justify-center gap-2 shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Verifying credentials...</span>
              </>
            ) : (
              <>
                <span>Unlock Workstation</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Switch User / Sign Out Footer */}
        <div className="mt-8 pt-5 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
          <span className="text-[11px]">Not {userProfile?.full_name?.split(' ')?.[0] || 'your session'}?</span>
          <button
            type="button"
            onClick={() => handleHardLogout('switch_user')}
            className="flex items-center gap-1.5 text-rose-400 hover:text-rose-300 font-medium transition-colors hover:underline"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Switch User / Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LockScreenOverlay;
